import type { JournalLine } from "@/apis/gql/types";
import { gql, useMutation } from "@apollo/client";
import { noop } from "lodash";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

type UpdateJournalLineInput = {
  journalEntryId: string;
  journalLineId: string;
  journalAccountId?: string;
  amount?: number;
  side?: "debit" | "credit";
  currency?: string;
  timestamp?: Date;
  taxCodeId?: string | null;
  costCenterId?: string | null;
};

const UPDATE_JOURNAL_LINES_MUTATION = gql`
  mutation UpdateJournalLine(
    $journalEntryId: String
    $journalLineId: String
    $journalAccountId: String
    $amount: Float
    $side: String
    $currency: String
    $timestamp: DateType
    $taxCodeId: String
    $costCenterId: String
  ) {
    updateJournalLine(
      journalEntryId: $journalEntryId
      journalLineId: $journalLineId
      journalAccountId: $journalAccountId
      amount: $amount
      side: $side
      currency: $currency
      timestamp: $timestamp
      taxCodeId: $taxCodeId
      costCenterId: $costCenterId
    )
  }
`;

export const useUpdateJournalLines = () => {
  const { t } = useTranslation();
  const [updateJournalLines, { data, loading, error }] = useMutation(
    UPDATE_JOURNAL_LINES_MUTATION,
    {
      refetchQueries: ["GetAllJournalEntries"],
    },
  );

  const handleUpdateJournalLines = async (
    data: UpdateJournalLineInput,
    onSuccess: (data: JournalLine) => void = noop,
    onError: () => void = noop,
  ) => {
    toast.dismiss();

    const toastId = toast.loading(t("generic_loading"));

    try {
      const response = await updateJournalLines({
        variables: {
          journalEntryId: data.journalEntryId,
          journalLineId: data.journalLineId,
          journalAccountId: data.journalAccountId,
          amount: data.amount,
          side: data.side,
          currency: data.currency,
          timestamp: data.timestamp,
          taxCodeId: data.taxCodeId,
          costCenterId: data.costCenterId,
        },
      });

      if (response.errors) {
        throw new Error("error updating journal lines");
      }

      onSuccess(response.data.updateJournalLines);

      toast.dismiss(toastId);

      return response;
    } catch (error) {
      onError();
      toast.error(t("generic_error"), {
        id: toastId,
        richColors: true,
      });
      throw error;
    }
  };

  return [handleUpdateJournalLines, { data, loading, error }] as const;
};
